import { AppNode, NodeType, NodeStatus, PanoramaQuality, PanoramaQualityDimensions } from '../types';
import { generateApi, GenerateRequest, GenerateResult, TaskStatusResult } from '../services/generateApi';
import { isBackendAssetUrl, saveAssets, createMediaUpdate, saveSingleAsset } from '../utils/assetUtils';
import { API_BASE } from '../config';

export interface GenerationContext {
    projectId: string | null;
    onProgress: (id: string, data: Partial<AppNode['data']>) => void;
    onAssetGenerated: (type: 'image' | 'video' | 'audio', src: string, title: string) => void;
    onTaskCreated: (taskId: string, nodeId: string, nodeType: string, request: Record<string, any>) => void;
}

export interface GenerationStrategy {
    canHandle: (nodeType: NodeType) => boolean;
    buildRequest: (node: AppNode, inputs: AppNode[], prompt: string) => GenerateRequest;
    processResult: (node: AppNode, result: GenerateResult, ctx: GenerationContext, prompt: string) => Promise<Partial<AppNode['data']>>;
}

const imageGeneratorStrategy: GenerationStrategy = {
    canHandle: (t) => t === NodeType.IMAGE_GENERATOR,
    buildRequest: (node, inputs, prompt) => {
        const inputImages: string[] = [];
        inputs.forEach(n => { if (n?.data.image) inputImages.push(n.data.image); });
        const hasImageInput = inputImages.length > 0;

        return {
            modelId: node.data.model || 'gemini-2.5-flash-image',
            prompt: prompt || '\u8FDB\u884C\u521B\u4F5C',
            mode: hasImageInput ? 'image-to-image' : 'text-to-image',
            inputImages: hasImageInput ? inputImages : undefined,
            params: {
                aspectRatio: node.data.aspectRatio || '16:9',
                resolution: node.data.resolution || '1k',
                imageCount: node.data.imageCount || 1,
            },
        };
    },
    processResult: async (node, result, ctx, prompt) => {
        if (!result.results || result.results.length === 0) {
            throw new Error('\u672A\u6536\u5230\u751F\u6210\u7684\u56FE\u7247');
        }

        const urls = result.results.map(r => r.url).filter((u): u is string => !!u);
        const hasImageInput = node.data.generationMode === 'IMG2IMG' || node.data.generationMode === 'UPSCALE';

        const { savedUrls } = await saveAssets(ctx.projectId, urls, {
            nodeId: node.id,
            prefix: 'image',
            metadata: {
                generator_node_id: node.id,
                generator_type: hasImageInput ? 'i2i' : 't2i',
                prompt,
                model: node.data.model || '',
            },
        });

        return createMediaUpdate(savedUrls, false);
    }
};

const videoGeneratorStrategy: GenerationStrategy = {
    canHandle: (t) => t === NodeType.VIDEO_GENERATOR,
    buildRequest: (node, inputs, prompt) => {
        const inputImages: string[] = [];
        inputs.forEach(n => { if (n?.data.image) inputImages.push(n.data.image); });
        const hasImageInput = inputImages.length > 0;

        return {
            modelId: node.data.model || 'veo-3.1-generate-preview',
            prompt: prompt || '\u751F\u6210\u4E00\u6BB5\u89C6\u9891',
            mode: hasImageInput ? 'image-to-video' : 'text-to-video',
            inputImages: hasImageInput ? inputImages : undefined,
            params: {
                aspectRatio: node.data.aspectRatio || '16:9',
                duration: node.data.duration || 5,
                videoCount: node.data.videoCount || 1,
                generationMode: node.data.generationMode,
                resolution: node.data.resolution,
                generateAudio: node.data.generateAudio,
            },
        };
    },
    processResult: async (node, result, ctx, prompt) => {
        let updates: Partial<AppNode['data']> = {};

        if (result.error) {
            updates = { error: result.error };
        } else if (result.results && result.results.length > 0) {
            const videoResult = result.results[0]!;
            if (videoResult.outputType === 'image') {
                const finalUrl = await saveSingleAsset(ctx.projectId, videoResult.url || '', 'fallback_image.png', {
                    generator_node_id: node.id,
                    generator_type: 'video_fallback',
                    prompt,
                    model: node.data.model || '',
                });
                updates = { image: finalUrl, videoUri: undefined, error: "Region restricted: Generated preview image instead." };
            } else {
                const finalUrl = await saveSingleAsset(ctx.projectId, videoResult.url || '', 'video.mp4', {
                    generator_node_id: node.id,
                    generator_type: 'video',
                    prompt,
                    model: node.data.model || '',
                    generation_mode: node.data.generationMode || '',
                }, true);
                updates = { videoUri: finalUrl, videoMetadata: result.videoMetadata };
            }
        } else {
            // 视频任务完成但没有结果 URL
            updates = { error: '视频生成完成但未返回视频结果，请重试或更换模型' };
        }

        if (node.data.generateAudio && result.results) {
            const audioResult = result.results.find(r => r.outputType === 'audio');
            if (audioResult) {
                const finalAudioUrl = await saveSingleAsset(ctx.projectId, audioResult.url || '', 'audio.wav', {
                    generator_node_id: node.id,
                    generator_type: 'audio',
                    prompt,
                });
                updates.audioUri = finalAudioUrl;
            }
        }

        return updates;
    }
};

const audioGeneratorStrategy: GenerationStrategy = {
    canHandle: (t) => t === NodeType.AUDIO_GENERATOR,
    buildRequest: (node, inputs, prompt) => {
        return {
            modelId: node.data.model || 'gemini-2.5-flash',
            prompt: prompt || '\u751F\u6210\u97F3\u9891',
            mode: 'text-to-audio',
            params: {
                duration: node.data.duration,
            },
        };
    },
    processResult: async (node, result, ctx, prompt) => {
        if (!result.results || result.results.length === 0) {
            throw new Error('\u672A\u6536\u5230\u751F\u6210\u7684\u97F3\u9891');
        }

        const finalUrl = await saveSingleAsset(ctx.projectId, result.results[0]?.url || '', 'audio.wav', {
            generator_node_id: node.id,
            generator_type: 'audio',
            prompt,
            model: node.data.model || '',
        });

        return { audioUri: finalUrl };
    }
};

const videoAnalyzerStrategy: GenerationStrategy = {
    canHandle: (t) => t === NodeType.VIDEO_ANALYZER,
    buildRequest: (node, inputs, prompt) => {
        const vid = node.data.videoUri || inputs.find(n => n?.data.videoUri)?.data.videoUri;
        if (!vid) throw new Error("\u672A\u627E\u5230\u89C6\u9891\u8F93\u5165");

        return {
            modelId: node.data.model || 'gemini-2.5-flash',
            prompt: prompt || '\u5206\u6790\u8FD9\u6BB5\u89C6\u9891',
            mode: 'video-analysis',
            inputImages: [vid],
            params: {},
        };
    },
    processResult: async (node, result, ctx, prompt) => {
        return { analysis: result.analysis || result.results?.[0]?.url || '' };
    }
};

const imageEditorStrategy: GenerationStrategy = {
    canHandle: (t) => t === NodeType.IMAGE_EDITOR,
    buildRequest: (node, inputs, prompt) => {
        const inputImages: string[] = [];
        inputs.forEach(n => { if (n?.data.image) inputImages.push(n.data.image); });
        const img = node.data.image || inputImages[0];

        return {
            modelId: node.data.model || 'gemini-2.5-flash-image',
            prompt: prompt || '\u7F16\u8F91\u56FE\u7247',
            mode: 'image-editing',
            inputImages: img ? [img] : undefined,
            params: {},
        };
    },
    processResult: async (node, result, ctx, prompt) => {
        if (!result.results || result.results.length === 0) {
            throw new Error('\u672A\u6536\u5230\u7F16\u8F91\u540E\u7684\u56FE\u7247');
        }

        const finalUrl = await saveSingleAsset(ctx.projectId, result.results[0]?.url || '', 'edited_image.png', {
            generator_node_id: node.id,
            generator_type: 'image_editor',
            prompt,
            model: node.data.model || '',
        });

        return { image: finalUrl };
    }
};

const panoramaGeneratorStrategy: GenerationStrategy = {
    canHandle: (t) => t === NodeType.PANORAMA_GENERATOR,
    buildRequest: (node, inputs, prompt) => {
        const inputImages: string[] = [];
        inputs.forEach(n => { if (n?.data.image) inputImages.push(n.data.image); });
        const referenceImage = node.data.image || inputImages[0];

        return {
            modelId: node.data.model || 'gemini-2.5-flash-image',
            prompt: prompt || '\u751F\u6210\u4E00\u5F20720\u00B0\u5168\u666F\u56FE',
            mode: referenceImage ? 'img2panorama' : 'txt2panorama',
            inputImages: referenceImage ? [referenceImage] : undefined,
            params: {
                quality: node.data.panoramaQuality || 'hd',
                aspectRatio: '2:1',
            },
        };
    },
    processResult: async (node, result, ctx, prompt) => {
        if (!result.results || result.results.length === 0) {
            throw new Error('\u672A\u6536\u5230\u751F\u6210\u7684\u5168\u666F\u56FE');
        }

        let finalUrl: string = result.results[0]?.url || '';
        if (!finalUrl) throw new Error('\u672A\u6536\u5230\u751F\u6210\u7684\u5168\u666F\u56FE');
        if (ctx.projectId && !isBackendAssetUrl(finalUrl)) {
            finalUrl = await saveSingleAsset(ctx.projectId, finalUrl, 'panorama.png', {
                generator_node_id: node.id,
                generator_type: 'panorama',
                prompt,
                model: node.data.model || '',
            });
        }

        ctx.onAssetGenerated('image', finalUrl, prompt);

        return {
            image: finalUrl,
            panoramaOutputUrl: finalUrl,
            panoramaOutputDimensions: PanoramaQualityDimensions[node.data.panoramaQuality || PanoramaQuality.HD],
            isPanoramaPreviewOpen: true,
        };
    }
};

const strategies: GenerationStrategy[] = [
    imageGeneratorStrategy,
    videoGeneratorStrategy,
    audioGeneratorStrategy,
    videoAnalyzerStrategy,
    imageEditorStrategy,
    panoramaGeneratorStrategy,
];

export const findStrategy = (nodeType: NodeType): GenerationStrategy | undefined => {
    return strategies.find(s => s.canHandle(nodeType));
};

export async function executeGeneration(
    node: AppNode,
    inputs: AppNode[],
    prompt: string,
    ctx: GenerationContext,
): Promise<Partial<AppNode['data']>> {
    const strategy = findStrategy(node.type);
    if (!strategy) throw new Error(`No strategy for node type: ${node.type}`);

    const request = strategy.buildRequest(node, inputs, prompt);

    const asyncResult = await generateApi.generateAsync(request);
    const taskId = asyncResult.taskId;

    ctx.onTaskCreated(taskId, node.id, node.type, request as Record<string, any>);

    const statusResult = await generateApi.pollTaskUntilComplete(taskId, (status) => {
        if (status.status === 'running') {
            ctx.onProgress(node.id, { progress: '\u751F\u6210\u4E2D...' });
        }
    });

    if (statusResult.status === 'failed') {
        throw new Error(statusResult.error || '\u4EFB\u52A1\u5931\u8D25');
    }
    if (statusResult.status === 'cancelled') {
        throw new Error('\u4EFB\u52A1\u5DF2\u53D6\u6D88');
    }

    const resultData = statusResult.result;
    if (!resultData) {
        throw new Error('\u672A\u6536\u5230\u751F\u6210\u7ED3\u679C');
    }

    const rawResult: GenerateResult = {
        taskId: statusResult.taskId,
        status: statusResult.status,
        results: resultData.results,
        error: resultData.error,
        videoMetadata: resultData.videoMetadata,
        analysis: resultData.analysis,
    };

    return strategy.processResult(node, rawResult, ctx, prompt);
}

export { imageGeneratorStrategy, videoGeneratorStrategy, audioGeneratorStrategy, videoAnalyzerStrategy, imageEditorStrategy, panoramaGeneratorStrategy };
